
import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Bell, BellRing, Zap } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format, parseISO } from "date-fns";
import { formatDeviceId } from '../utils/deviceUtils';

type MotionEvent = {
  id: string;
  created_at: string;
  device_id: string;
  message: string;
  forwarded: boolean;
  processed?: boolean;
  device_name?: string; // Optional device name from join
};

interface MotionAlertsProps {
  deviceId: string;
}

export function MotionAlerts({ deviceId }: MotionAlertsProps) {
  const [motionEvents, setMotionEvents] = useState<MotionEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const [deviceNames, setDeviceNames] = useState<Record<string, string>>({});
  const [debugInfo, setDebugInfo] = useState<string>("");

  // Add debug information about the current deviceId being displayed
  useEffect(() => {
    console.log(`MotionAlerts component initialized for device: ${deviceId}`);
    setDebugInfo(`Monitoring device: ${deviceId}`);
  }, [deviceId]);

  useEffect(() => {
    const fetchDeviceNames = async () => {
      const { data, error } = await supabase
        .from('devices')
        .select('device_id, name');
      
      if (error) {
        console.error("Error fetching device names:", error);
        return;
      }
      
      const namesMap: Record<string, string> = {};
      data?.forEach(device => {
        namesMap[device.device_id] = device.name || `Device ${formatDeviceId(device.device_id, true)}`;
      });
      
      setDeviceNames(namesMap);
      console.log("Device names loaded:", namesMap);
    };
    
    fetchDeviceNames();
  }, []);

  useEffect(() => {
    const fetchMotionEvents = async () => {
      setLoading(true);
      try {
        // Log the device ID we're querying for to help debug
        console.log(`Fetching motion events for device: ${deviceId}`);
        
        const { data, error } = await supabase
          .from('led_triggers')
          .select('*')
          .eq('device_id', deviceId)
          .order('created_at', { ascending: false })
          .limit(5);

        if (error) {
          console.error(`Error fetching motion events for ${deviceId}:`, error);
          throw error;
        }
        
        // Debug: Log the data received from Supabase
        console.log(`Found ${data?.length || 0} motion events for device ${deviceId}`);
        
        const formattedEvents = data?.map(event => ({
          ...event,
          forwarded: event.forwarded || false,
          device_name: deviceNames[event.device_id] || `Device ${formatDeviceId(event.device_id, true)}`
        })) || [];
        
        setMotionEvents(formattedEvents);
      } catch (error) {
        console.error("Error fetching motion events:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMotionEvents();

    // Set up real-time subscription for this specific device
    console.log(`Setting up real-time subscription for device: ${deviceId}`);
    
    const channel = supabase
      .channel(`led_triggers_${deviceId}`) // Make channel name unique per device
      .on(
        'postgres_changes',
        { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'led_triggers',
          filter: `device_id=eq.${deviceId}` // Add filter for this specific device
        },
        (payload) => {
          console.log(`New motion event received for device ${deviceId}:`, payload);
          
          // Extra verification that this event is for our device
          if ((payload.new as any).device_id === deviceId) {
            setMotionEvents(prev => {
              const newEvent = {
                ...payload.new as MotionEvent,
                forwarded: (payload.new as any).forwarded || false,
                device_name: deviceNames[deviceId] || `Device ${formatDeviceId(deviceId, true)}`
              };
              const updated = [newEvent, ...prev.slice(0, 4)];
              
              toast({
                title: "Motion Detected!",
                description: `${newEvent.device_name} detected motion`,
                variant: "default",
              });
              
              forwardMotionEvent(newEvent.device_id, newEvent.id);
              
              return updated;
            });
          } else {
            console.log(`Received event for wrong device. Expected ${deviceId}, got ${(payload.new as any).device_id}`);
          }
        }
      )
      .subscribe((status) => {
        console.log(`Subscription status for device ${deviceId}:`, status);
      });

    return () => {
      console.log(`Cleaning up subscription for device ${deviceId}`);
      supabase.removeChannel(channel);
    };
  }, [toast, deviceNames, deviceId]);

  const forwardMotionEvent = async (sourceDeviceId: string, eventId: string) => {
    try {
      const { data: devices, error: devicesError } = await supabase
        .from('devices')
        .select('device_id')
        .neq('device_id', sourceDeviceId);

      if (devicesError) throw devicesError;
      
      if (!devices || devices.length === 0) {
        console.log("No other devices to forward motion event to");
        return;
      }
      
      console.log(`Forwarding motion event from ${sourceDeviceId} to ${devices.length} other devices`);
      
      for (const device of devices) {
        const { error: cmdError } = await supabase
          .from('device_commands')
          .insert({
            device_id: device.device_id,
            command_type: 'motion_trigger',
            params: JSON.stringify({
              source_device: sourceDeviceId,
              trigger_id: eventId,
              timestamp: new Date().toISOString()
            }),
            processed: false
          });
          
        if (cmdError) {
          console.error(`Error sending command to device ${device.device_id}:`, cmdError);
        }
      }
      
      await supabase
        .from('led_triggers')
        .update({ forwarded: true })
        .eq('id', eventId);
        
      console.log(`Successfully forwarded motion event ${eventId} to all other devices`);
      
    } catch (error) {
      console.error("Error forwarding motion event:", error);
    }
  };

  const handleManualForward = async (event: MotionEvent) => {
    if (event.forwarded) {
      toast({
        title: "Already Forwarded",
        description: "This motion event has already been forwarded to other devices",
        variant: "default",
      });
      return;
    }
    
    setLoading(true);
    try {
      await forwardMotionEvent(event.device_id, event.id);
      
      setMotionEvents(prev => 
        prev.map(e => e.id === event.id ? {...e, forwarded: true} : e)
      );
      
      const deviceName = deviceNames[event.device_id] || `Device ${formatDeviceId(event.device_id, true)}`;
      
      toast({
        title: "Motion Event Forwarded",
        description: `Successfully notified all other devices about motion from ${deviceName}`,
        variant: "default",
      });
    } catch (error) {
      console.error("Error in manual forward:", error);
      toast({
        title: "Forward Failed",
        description: "Could not forward motion event to other devices",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timestamp: string) => {
    try {
      return format(parseISO(timestamp), 'HH:mm:ss');
    } catch (error) {
      return timestamp;
    }
  };

  const formatDeviceDisplay = (deviceId: string) => {
    const deviceName = deviceNames[deviceId] || `Device ${formatDeviceId(deviceId, true)}`;
    return (
      <div className="flex flex-col">
        <span className="font-medium">{deviceName}</span>
        <span className="text-xs text-muted-foreground font-mono">{deviceId}</span>
      </div>
    );
  };

  // Add a manual refresh button for testing/debugging
  const handleManualRefresh = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('led_triggers')
        .select('*')
        .eq('device_id', deviceId)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      
      const formattedEvents = data?.map(event => ({
        ...event,
        forwarded: event.forwarded || false,
        device_name: deviceNames[event.device_id] || `Device ${formatDeviceId(event.device_id, true)}`
      })) || [];
      
      setMotionEvents(formattedEvents);
      
      toast({
        title: "Refreshed",
        description: `Found ${formattedEvents.length} events for ${formatDeviceId(deviceId, true)}`,
      });
    } catch (error) {
      console.error("Manual refresh error:", error);
      toast({
        title: "Refresh Failed",
        description: "Could not refresh motion events",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="py-3">
        <CardTitle className="flex items-center justify-between text-md">
          <div className="flex items-center gap-2">
            <BellRing className="h-5 w-5" /> 
            Motion Alerts
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleManualRefresh} 
            disabled={loading}
            className="h-7 px-2"
          >
            Refresh
          </Button>
        </CardTitle>
        <CardDescription>
          Recent motion events
        </CardDescription>
      </CardHeader>
      <CardContent className="px-4 pb-3">
        {debugInfo && import.meta.env.DEV && (
          <div className="text-xs mb-2 p-1 bg-muted rounded">
            {debugInfo}
          </div>
        )}
        
        {loading && motionEvents.length === 0 ? (
          <div className="text-center py-3 text-muted-foreground">Loading motion events...</div>
        ) : motionEvents.length === 0 ? (
          <div className="text-center py-3 text-muted-foreground">No motion events detected yet</div>
        ) : (
          <div className="space-y-2">
            {motionEvents.map((event) => (
              <div key={event.id} className="flex items-center justify-between border-b pb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Bell className="h-4 w-4 flex-shrink-0" />
                    <div className="font-medium">{event.device_name}</div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {formatTime(event.created_at)}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {event.forwarded ? (
                    <Badge variant="outline" className="bg-green-50">
                      Forwarded
                    </Badge>
                  ) : (
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleManualForward(event)}
                      disabled={loading}
                      className="h-7 text-xs"
                    >
                      <Zap className="h-3 w-3 mr-1" />
                      Forward
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
